import { memo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionCable from 'actioncable';
// Local files
import { wsURL } from 'apis';
import { cableMessageTypes } from 'helpers/constants';
import useMessages from 'hooks/useMessages';
import useCustomSelector from 'hooks/useCustomSelector';
import usePatients from 'hooks/usePatients';
import useConversations from 'hooks/useConversations';
import useAppointmentAnalyses from 'hooks/useAppointmentAnalyses';
import useFirstAnalyses from 'hooks/useFirstAnalyses';
import useSecondAnalyses from 'hooks/useSecondAnalyses';
import useThirdAnalyses from 'hooks/useThirdAnalyses';
import useFifthAnalyses from 'hooks/useFifthAnalyses';
import useSixthAnalyses from 'hooks/useSixthAnalyses';
import useStudyAnalyses from 'hooks/useStudyAnalyses';
import useSeventhAnalyses from 'hooks/useSeventhAnalyses';
import useWaterfallAnalyses from 'hooks/useWaterfallAnalyses';
import useFillAnalyses from 'hooks/useFillAnalyses';
import useEsignAnalyses from 'hooks/useEsignAnalyses';
import useVoiceAnalyses from 'hooks/useVoiceAnalyses';
import useReminderAnalyses from 'hooks/useReminderAnalyses';
import useReferralAnalyses from 'hooks/useReferralAnalyses';
import useScheduleAnalyses from 'hooks/useScheduleAnalyses';
import useConfirmAnalyses from 'hooks/useConfirmAnalyses';
import usePenStudyAnalyses from 'hooks/usePenStudyAnalyses';
import useSuperbillAnalyses from 'hooks/useSuperbillAnalyses';
import useBookAnalyses from 'hooks/useBookAnalyses';
import usePatientAnalyses from 'hooks/usePatientAnalyses';
import { patientsSelectors } from 'features/patients';
import { sessionsSelectors } from 'features/sessions';
import { conversationsSelectors } from 'features/conversations';

const {
  MESSAGE_CREATED,
  MESSAGE_DESTROYED,
  PATIENT_CREATED,
  PATIENT_UPDATED,
  PATIENT_DESTROYED,
  CONVERSATION_UPDATED,
  APPOINTMENT_ANALYSIS_STATUS_UPDATED,
  APPOINTMENT_ANALYSIS_CREATED,
  FIRST_ANALYSIS_STATUS_UPDATED,
  FIRST_ANALYSIS_CREATED,
  SECOND_ANALYSIS_STATUS_UPDATED,
  SECOND_ANALYSIS_CREATED,
  THIRD_ANALYSIS_STATUS_UPDATED,
  THIRD_ANALYSIS_CREATED,
  FIFTH_ANALYSIS_STATUS_UPDATED,
  FIFTH_ANALYSIS_CREATED,
  SIXTH_ANALYSIS_STATUS_UPDATED,
  SIXTH_ANALYSIS_CREATED,
  STUDY_ANALYSIS_STATUS_UPDATED,
  STUDY_ANALYSIS_CREATED,
  SEVENTH_ANALYSIS_STATUS_UPDATED,
  SEVENTH_ANALYSIS_CREATED,
  WATERFALL_ANALYSIS_STATUS_UPDATED,
  WATERFALL_ANALYSIS_CREATED,
  FILL_ANALYSIS_STATUS_UPDATED,
  FILL_ANALYSIS_CREATED,
  ESIGN_ANALYSIS_STATUS_UPDATED,
  ESIGN_ANALYSIS_CREATED,
  VOICE_ANALYSIS_STATUS_UPDATED,
  VOICE_ANALYSIS_CREATED,
  REMINDER_ANALYSIS_STATUS_UPDATED,
  REMINDER_ANALYSIS_CREATED,
  REFERRAL_ANALYSIS_STATUS_UPDATED,
  REFERRAL_ANALYSIS_CREATED,
  SCHEDULE_ANALYSIS_STATUS_UPDATED,
  SCHEDULE_ANALYSIS_CREATED,
  CONFIRM_ANALYSIS_STATUS_UPDATED,
  CONFIRM_ANALYSIS_CREATED,
  PEN_STUDY_ANALYSIS_STATUS_UPDATED,
  PEN_STUDY_ANALYSIS_CREATED,
  SUPERBILL_ANALYSIS_STATUS_UPDATED,
  SUPERBILL_ANALYSIS_CREATED,
  BOOK_ANALYSIS_STATUS_UPDATED,
  BOOK_ANALYSIS_CREATED,
  PATIENT_ANALYSIS_STATUS_UPDATED,
  PATIENT_ANALYSIS_CREATED
} = cableMessageTypes;

const Cable = () => {
  const navigate = useNavigate();
  const { getMessages, getMessage, deleteLocalMessage, clearLocalMessages } = useMessages();
  const { getPatient, deleteLocalPatient, clearLocalActivePatient } = usePatients();
  const { getConversation, clearLocalConversations, clearLocalActiveConversation } = useConversations();
  const { getAppointmentAnalysis } = useAppointmentAnalyses();
  const { getFirstAnalysis } = useFirstAnalyses();
  const { getSecondAnalysis } = useSecondAnalyses();
  const { getThirdAnalysis } = useThirdAnalyses();
  const { getFifthAnalysis } = useFifthAnalyses();
  const { getSixthAnalysis } = useSixthAnalyses();
  const { getStudyAnalysis } = useStudyAnalyses();
  const { getSeventhAnalysis } = useSeventhAnalyses();
  const { getWaterfallAnalysis } = useWaterfallAnalyses();
  const { getFillAnalysis } = useFillAnalyses();
  const { getEsignAnalysis } = useEsignAnalyses();
  const { getVoiceAnalysis } = useVoiceAnalyses();
  const { getReminderAnalysis } = useReminderAnalyses();
  const { getReferralAnalysis } = useReferralAnalyses();
  const { getScheduleAnalysis } = useScheduleAnalyses();
  const { getConfirmAnalysis } = useConfirmAnalyses();
  const { getPenStudyAnalysis } = usePenStudyAnalyses();
  const { getSuperbillAnalysis } = useSuperbillAnalyses();
  const { getBookAnalysis } = useBookAnalyses();
  const { getPatientAnalysis } = usePatientAnalyses();
  const localSession = useCustomSelector(sessionsSelectors.getManagerSession);
  const { id: patientId, mode } = useCustomSelector(patientsSelectors.getPatientData);
  const patientIsDeleting = useCustomSelector(patientsSelectors.patientIsDeleting);
  const { id: conversationId, mode: conversationMode } = useCustomSelector(conversationsSelectors.getConversation);
  const updateConversation = useCustomSelector(conversationsSelectors.isUpdateConversation);
  const devMode = useCustomSelector(conversationsSelectors.getDevMode);

  const fetchMessages = useCallback(({ conversation_id, mode, devMode }) => {
    clearLocalMessages();
    getMessages({ 
      conversation_id, 
      senders: devMode ? 
              ['patient', 'manager', 'bot', 'system'] : 
              mode === 'manual' ? ['patient', 'manager'] : ['patient', 'manager', 'bot'] 
    });   
  }, [getMessages, clearLocalMessages]);
  const handleMessageCreated = useCallback(({ conversation_id, message_id, sender }) => {
    if (conversation_id === conversationId && sender !== 'manager' && (mode !== 'manual' || devMode || (mode === 'manual' && sender === 'patient'))) {
      getMessage({ cable: true, message_id })
    }
  }, [conversationId, mode, devMode, getMessage]);
  const handleMessageDestroyed = useCallback(({ conversation_id, message_id }) => {
    if (conversation_id === conversationId) {
      deleteLocalMessage(message_id);
    }
  }, [conversationId, deleteLocalMessage]);
  const handlePatient = useCallback(({ patient_id }) => getPatient({ id: patient_id, cable: true }), [getPatient]);
  const handlePatientDestroyed = useCallback(({ patient_id }) => {
    if (!patientIsDeleting) {
      deleteLocalPatient(patient_id);
      if (patient_id === patientId) {
        clearLocalActivePatient();
        clearLocalMessages();
        clearLocalConversations();
        clearLocalActiveConversation();
        navigate('/patients');
      }
    } 
  }, [
    deleteLocalPatient,
    clearLocalActivePatient,
    clearLocalMessages,
    clearLocalConversations,
    clearLocalActiveConversation,
    navigate, 
    patientId,
    patientIsDeleting
  ]);
  const handleConversation = useCallback(({ conversation_id }) => {
    if (conversation_id === conversationId && !updateConversation) {
      getConversation(conversation_id)
      .then(({ payload, error }) => {
        if (!error) {
          const { conversation: { id, mode } } = payload;
          if (mode !== conversationMode) {
            fetchMessages({ conversation_id: id, mode, devMode })
          }
        }
      })
    }
  }, [getConversation, fetchMessages, conversationId, conversationMode, devMode, updateConversation]);
  const handleAppointmentAnalysis = useCallback(({ appointment_analysis_id }) => getAppointmentAnalysis({ id: appointment_analysis_id, cable: true }), [getAppointmentAnalysis]);
  const handleFirstAnalysis = useCallback(({ first_analysis_id }) => getFirstAnalysis({ id: first_analysis_id, cable: true }), [getFirstAnalysis]);
  const handleSecondAnalysis = useCallback(({ second_analysis_id }) => getSecondAnalysis({ id: second_analysis_id, cable: true }), [getSecondAnalysis]);
  const handleThirdAnalysis = useCallback(({ third_analysis_id }) => getThirdAnalysis({ id: third_analysis_id, cable: true }), [getThirdAnalysis]);
  const handleFifthAnalysis = useCallback(({ fifth_analysis_id }) => getFifthAnalysis({ id: fifth_analysis_id, cable: true }), [getFifthAnalysis]);
  const handleSixthAnalysis = useCallback(({ sixth_analysis_id }) => getSixthAnalysis({ id: sixth_analysis_id, cable: true }), [getSixthAnalysis]);
  const handleStudyAnalysis = useCallback(({ study_analysis_id }) => getStudyAnalysis({ id: study_analysis_id, cable: true }), [getStudyAnalysis]);
  const handleSeventhAnalysis = useCallback(({ seventh_analysis_id }) => getSeventhAnalysis({ id: seventh_analysis_id, cable: true }), [getSeventhAnalysis]);
  const handleWaterfallAnalysis = useCallback(({ waterfall_analysis_id }) => getWaterfallAnalysis({ id: waterfall_analysis_id, cable: true }), [getWaterfallAnalysis]);
  const handleFillAnalysis = useCallback(({ fill_analysis_id }) => getFillAnalysis({ id: fill_analysis_id, cable: true }), [getFillAnalysis]);
  const handleEsignAnalysis = useCallback(({ esign_analysis_id }) => getEsignAnalysis({ id: esign_analysis_id, cable: true }), [getEsignAnalysis]);
  const handleVoiceAnalysis = useCallback(({ voice_analysis_id }) => getVoiceAnalysis({ id: voice_analysis_id, cable: true }), [getVoiceAnalysis]);
  const handleReminderAnalysis = useCallback(({ reminder_analysis_id }) => getReminderAnalysis({ id: reminder_analysis_id, cable: true }), [getReminderAnalysis]);
  const handleReferralAnalysis = useCallback(({ referral_analysis_id }) => getReferralAnalysis({ id: referral_analysis_id, cable: true }), [getReferralAnalysis]);
  const handleScheduleAnalysis = useCallback(({ schedule_analysis_id }) => getScheduleAnalysis({ id: schedule_analysis_id, cable: true }), [getScheduleAnalysis]);
  const handleConfirmAnalysis = useCallback(({ confirm_analysis_id }) => getConfirmAnalysis({ id: confirm_analysis_id, cable: true }), [getConfirmAnalysis]);
  const handlePenStudyAnalysis = useCallback(({ pen_study_analysis_id }) => getPenStudyAnalysis({ id: pen_study_analysis_id, cable: true }), [getPenStudyAnalysis]);
  const handleSuperbillAnalysis = useCallback(({ superbill_analysis_id }) => getSuperbillAnalysis({ id: superbill_analysis_id, cable: true }), [getSuperbillAnalysis]);
  const handleBookAnalysis = useCallback(({ book_analysis_id }) => getBookAnalysis({ id: book_analysis_id, cable: true }), [getBookAnalysis]);
  const handlePatientAnalysis = useCallback(({ patient_analysis_id }) => getPatientAnalysis({ id: patient_analysis_id, cable: true }), [getPatientAnalysis]);
  const received = useCallback(data => {
    switch (data.type) {
      case MESSAGE_CREATED:
        handleMessageCreated(data);
        break;
      case MESSAGE_DESTROYED:
        handleMessageDestroyed(data);
        break;
      case PATIENT_CREATED:
        handlePatient(data);
        break;
      case PATIENT_UPDATED:
        handlePatient(data);
        break;
      case PATIENT_DESTROYED:
        handlePatientDestroyed(data);
        break;
      case CONVERSATION_UPDATED:
        handleConversation(data);
        break;
      case APPOINTMENT_ANALYSIS_STATUS_UPDATED:
        handleAppointmentAnalysis(data);
        break;
      case APPOINTMENT_ANALYSIS_CREATED:
        handleAppointmentAnalysis(data);
        break;
      case FIRST_ANALYSIS_STATUS_UPDATED:
        handleFirstAnalysis(data);
        break;
      case FIRST_ANALYSIS_CREATED:
        handleFirstAnalysis(data);
        break;
      case SECOND_ANALYSIS_STATUS_UPDATED:
        handleSecondAnalysis(data);
        break;
      case SECOND_ANALYSIS_CREATED:
        handleSecondAnalysis(data);
        break;
      case THIRD_ANALYSIS_STATUS_UPDATED:
        handleThirdAnalysis(data);
        break;
      case THIRD_ANALYSIS_CREATED:
        handleThirdAnalysis(data);
        break;
      case FIFTH_ANALYSIS_STATUS_UPDATED:
        handleFifthAnalysis(data);
        break;
      case FIFTH_ANALYSIS_CREATED:
        handleFifthAnalysis(data);
        break;
      case SIXTH_ANALYSIS_STATUS_UPDATED:
        handleSixthAnalysis(data);
        break;
      case SIXTH_ANALYSIS_CREATED:
        handleSixthAnalysis(data);
        break;
      case STUDY_ANALYSIS_STATUS_UPDATED:
        handleStudyAnalysis(data);
        break;
      case STUDY_ANALYSIS_CREATED:
        handleStudyAnalysis(data);
        break;
      case SEVENTH_ANALYSIS_STATUS_UPDATED:
        handleSeventhAnalysis(data);
        break;
      case SEVENTH_ANALYSIS_CREATED:
        handleSeventhAnalysis(data);
        break;
      case WATERFALL_ANALYSIS_STATUS_UPDATED:
        handleWaterfallAnalysis(data);
        break;
      case WATERFALL_ANALYSIS_CREATED:
        handleWaterfallAnalysis(data);
        break;
      case FILL_ANALYSIS_STATUS_UPDATED:
        handleFillAnalysis(data);
        break;
      case FILL_ANALYSIS_CREATED:
        handleFillAnalysis(data);
        break;
      case ESIGN_ANALYSIS_STATUS_UPDATED:
        handleEsignAnalysis(data);
        break;
      case ESIGN_ANALYSIS_CREATED:
        handleEsignAnalysis(data);
        break;
      case VOICE_ANALYSIS_STATUS_UPDATED:
        handleVoiceAnalysis(data);
        break;
      case VOICE_ANALYSIS_CREATED:
        handleVoiceAnalysis(data);
        break;
      case REMINDER_ANALYSIS_STATUS_UPDATED:
        handleReminderAnalysis(data);
        break;
      case REMINDER_ANALYSIS_CREATED:
        handleReminderAnalysis(data);
        break;
      case REFERRAL_ANALYSIS_STATUS_UPDATED:
        handleReferralAnalysis(data);
        break;
      case REFERRAL_ANALYSIS_CREATED:
        handleReferralAnalysis(data);
        break;
      case SCHEDULE_ANALYSIS_STATUS_UPDATED:
        handleScheduleAnalysis(data);
        break;
      case SCHEDULE_ANALYSIS_CREATED:
        handleScheduleAnalysis(data);
        break;
      case CONFIRM_ANALYSIS_STATUS_UPDATED:
        handleConfirmAnalysis(data);
        break;
      case CONFIRM_ANALYSIS_CREATED:
        handleConfirmAnalysis(data);
        break;
      case PEN_STUDY_ANALYSIS_STATUS_UPDATED:
        handlePenStudyAnalysis(data);
        break;
      case PEN_STUDY_ANALYSIS_CREATED:
        handlePenStudyAnalysis(data);
        break;
      case SUPERBILL_ANALYSIS_STATUS_UPDATED:
        handleSuperbillAnalysis(data);
        break;
      case SUPERBILL_ANALYSIS_CREATED:
        handleSuperbillAnalysis(data);
        break;
      case BOOK_ANALYSIS_STATUS_UPDATED:
        handleBookAnalysis(data);
        break;
      case BOOK_ANALYSIS_CREATED:
        handleBookAnalysis(data);
        break;
      case PATIENT_ANALYSIS_STATUS_UPDATED:
        handlePatientAnalysis(data);
        break;
      case PATIENT_ANALYSIS_CREATED:
        handlePatientAnalysis(data);
        break;
      default:
        break;
    };
  }, [
    handleMessageCreated,
    handleMessageDestroyed,
    handlePatient,
    handlePatientDestroyed, 
    handleConversation,
    handleAppointmentAnalysis, 
    handleFirstAnalysis, 
    handleSecondAnalysis, 
    handleThirdAnalysis, 
    handleFifthAnalysis, 
    handleSixthAnalysis,
    handleStudyAnalysis,
    handleSeventhAnalysis,
    handleWaterfallAnalysis,
    handleFillAnalysis,
    handleEsignAnalysis,
    handleVoiceAnalysis,
    handleReminderAnalysis,
    handleReferralAnalysis,
    handleScheduleAnalysis,
    handleConfirmAnalysis,
    handlePenStudyAnalysis,
    handleSuperbillAnalysis,
    handleBookAnalysis,
    handlePatientAnalysis
  ]);
  const rejected = useCallback((cable) => !!cable && cable.disconnect(), []);

  useEffect(() => {
    let cable = null;

    if (localSession.accessToken) {
      cable = ActionCable.createConsumer(`${wsURL}?access_token=${localSession.accessToken}`);
      cable.subscriptions.create('NotificationsChannel', { received, rejected: () => rejected(cable) });
    }    

    return () => {
      if (!!cable) cable.disconnect();
    };
  }, [localSession, received, rejected]);

  return null;
};

export default memo(Cable);